import React, { useEffect, useContext, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, Flex, Label, Text } from 'pcln-design-system'
import DateField from '../date-field'
import TimeField from '../time-field'
import TypeAhead from '../type-ahead/TypeAhead'
import { Calendar } from '@pcln/date-picker'
import { CalendarWrapperBox, FindYourDealButton, ShadowEffect } from '../../shared'
import moment from 'moment/moment'
import { pathOr, pipe } from 'ramda'
import { PopoverContainer } from '../Popover'
import pclnExperimentation from '@pcln/experimentation'
import { formatDate, getPackagesDateRange } from '../../utils/date-helpers'
import makeFeedbackAjaxCall from '../../utils/typeahead-feedback'
import BootstrapDataContext from '../../contexts/BootstrapDataContext'
import ProductTypeCheckbox from './ProductTypeCheckbox'
import { TravelerSelection } from '../traveler-selection'
import {
  CALENDAR_POPOVER_SELECTOR,
  DRIVE,
  HOTEL_FLIGHT_CAR,
  HOTEL_AND_CAR,
  FLIGHT_AND_CAR,
  FLY,
  RENTAL_CAR_PRODUCT_TYPE,
  STAY
} from '../../constants/Constants'

const config = require('isomorphic-config')

const noop = () => {}

const typeaheadFeedbackURL = config.client.typeahead.feedback.cars
const typeaheadFeedback = makeFeedbackAjaxCall(typeaheadFeedbackURL)

const flightEnabledProductsOption = {
  name: 'Flight',
  type: FLIGHT_AND_CAR,
  label: 'Add a flight',
  id: 'add-a-flight-checkbox',
  gaLabel: 'add flight'
}
const defaultProductsOption = {
  name: 'Hotel',
  type: HOTEL_AND_CAR,
  label: 'Add a hotel',
  id: 'add-a-hotel-checkbox',
  gaLabel: 'add hotel'
}

const startLocationPlaceholder = pathOr(
  'City, Airport or Address',
  [ 'contentValues', 'HOMEPAGE', 'HOMEPAGE.RENTALCARS.TAB.TYPEAHEAD.STARTLOCATION.PLACEHOLDER', 'value' ])
const endLocationPlaceholder = pathOr(
  'City, Airport or Address',
  [ 'contentValues', 'HOMEPAGE', 'HOMEPAGE.RENTALCARS.TAB.TYPEAHEAD.ENDLOCATION.PLACEHOLDER', 'value' ])

const computeTravelerSelectionLabel = (hotelAdded, values) => {
  const travelers = `${values.adultCount} Adults `
  const rooms = hotelAdded ? `, ${values.roomCount} Room` : ''
  return travelers + rooms
}

const focusInput = (inputRef) => {
  if (inputRef.current) {
    inputRef.current.focus()
  }
}

const RentalCarsSearchForm = ({
  values,
  touched,
  dirty,
  errors,
  handleChange,
  handleBlur,
  handleFormBlur,
  handleFormFocus,
  handleSubmit,
  setFieldTouched,
  setFieldValue,
  typeAheadOptions,
  calendarOpen,
  openCalendar,
  closeCalendar,
  showStartCalendar,
  showEndCalendar,
  toggleStartCalendar,
  toggleEndCalendar,
  handleTypeaheadOnFocus,
  handleCalendarOnFocus,
  handleSelectOnFocus,
  handleTrackingClick,
  role,
  id,
  ariaLabelledby,
  onTypeaheadKeyUp,
  handleTravelerSelectionOnFocus,
  withFooter,
  onTypeAheadConfigChange
}) => {
  const {
    aceContent = {},
    isMobile,
    seti: {
      HP_RC_LARGER_INPUTS = 'DEFAULT', // TODO: HP_RC_LARGER_INPUTS clean up
      HP_CARS_ADD_FLIGHT = 'DEFAULT'
    } = {}
  } = useContext(BootstrapDataContext)

  const [selectedProduct, setSelectedProduct] = useState([values.tripType])
  const hasHotel = values => values.tripType.includes(STAY)
  const hasFlight = values => values.tripType.includes(FLY)
  const hotelAdded = hasHotel(values)
  const flightAdded = hasFlight(values)
  const [travelerSelectionLabel, setTravelerSelectionLabel] = useState(() => computeTravelerSelectionLabel(hotelAdded, values))
  const isFlightCheckboxEnabled = HP_CARS_ADD_FLIGHT === 'ADD_FLIGHT'
  const handleOpenStartCalendar = () => toggleStartCalendar(true)
  const handleOpenEndCalendar = () => toggleEndCalendar(true)
  const handleCloseStartCalendar = () => toggleStartCalendar(false)
  const handleCloseEndCalendar = () => toggleEndCalendar(false)

  const productTypeCheckboxData = {
    selectedProducts: [DRIVE],
    productsOptions: [defaultProductsOption].concat(
      isFlightCheckboxEnabled ? [flightEnabledProductsOption] : []
    )
  }

  // TODO: HP_RC_LARGER_INPUTS clean up
  useEffect(() => {
    pclnExperimentation.fireImpression('HP_RC_LARGER_INPUTS')
    pclnExperimentation.fireImpression('HP_CARS_ADD_FLIGHT')
  }, [])
  useEffect(() => {
    values.endDate = hotelAdded && values.startDate === values.endDate ? '' : values.endDate
    setTravelerSelectionLabel(computeTravelerSelectionLabel(hotelAdded, values))
  }, [hotelAdded])

  useEffect(() => {
    if (values.tripType === HOTEL_FLIGHT_CAR) {
      setSelectedProduct([HOTEL_AND_CAR, FLIGHT_AND_CAR])
      onTypeAheadConfigChange('packages')
    } else if (values.tripType === HOTEL_AND_CAR) {
      setSelectedProduct([HOTEL_AND_CAR])
      onTypeAheadConfigChange('packages')
    } else if (values.tripType === FLIGHT_AND_CAR) {
      setSelectedProduct([FLIGHT_AND_CAR])
      onTypeAheadConfigChange('packages')
    } else {
      setSelectedProduct([values.tripType])
    }
  }, [values.tripType])

  // TODO: HP_RC_LARGER_INPUTS clean up
  const isLargeInput = HP_RC_LARGER_INPUTS !== 'DEFAULT'
  const largerInputStyle = { height: isLargeInput ? '56px' : null }
  const handleProductTypeChange = e => {
    const { value: type } = e.target
    if (e.target.checked) {
      setSelectedProduct((selectedProduct) => {
        const currentSelectedProduct = [...selectedProduct, type]
        if (currentSelectedProduct.includes(FLIGHT_AND_CAR) && currentSelectedProduct.includes(HOTEL_AND_CAR)) {
          setFieldValue('tripType', HOTEL_FLIGHT_CAR)
        } else {
          setFieldValue('tripType', type)
        }
        onTypeAheadConfigChange('packages')
        return currentSelectedProduct
      })
    } else {
      setSelectedProduct((selectedProduct) => {
        const currentSelectedProduct = selectedProduct.filter(product => product !== type)
        if (type === FLIGHT_AND_CAR && selectedProduct.includes(HOTEL_AND_CAR)) {
          setFieldValue('tripType', HOTEL_AND_CAR)
        } else if (type === HOTEL_AND_CAR && selectedProduct.includes(FLIGHT_AND_CAR)) {
          setFieldValue('tripType', FLIGHT_AND_CAR)
        } else {
          onTypeAheadConfigChange('cars')
          setFieldValue('tripType', DRIVE)
        }
        return currentSelectedProduct
      })
    }
  }

  const parseDateError = (touched, errors) => {
    const evalStartDate = () => touched.startDate && errors.startDate
    const evalEndDate = () => touched.endDate && errors.endDate
    return evalStartDate() || evalEndDate()
  }

  const VP_DISCLAIMER = 'You may only book a package for up to 8 travelers.'

  const handleTravelerSelectionChange = setFieldValue => obj => {
    if (obj.childAges) {
      const arr = Object.keys(obj.childAges).map(key => obj.childAges[key])
      setFieldValue('childrenAges', arr)
      return
    }
    switch (obj.travelerType) {
      case 'ROOMS':
        setFieldValue('roomCount', obj.count)
        break
      case 'ADULTS':
        setFieldValue('adultCount', obj.count)
        break
      case 'CHILDREN':
        setFieldValue('childrenCount', obj.count)
        break
    }
  }

  const pickupCalendarInput = useRef()
  const focusPickupCalendarInput = () => focusInput(pickupCalendarInput)
  const dropoffCalendarInput = useRef()
  const focusDropoffCalendarInput = () => focusInput(dropoffCalendarInput)

  return (
    <form onSubmit={handleSubmit} onFocus={handleFormFocus} onBlur={handleFormBlur} aria-labelledby={ariaLabelledby} role={role} id={id}>
      <Flex flexWrap='wrap' mx={-2}>
        {
          (!hotelAdded || flightAdded) &&
          <ShadowEffect forProperty='startLocation' width={values.oneWay || flightAdded ? [1, null, 1 / 2] : 1} px={2} mt={3} mb={2}>
            { ({ disableShadowState }) => (
              <TypeAhead
                id='startLocation'
                name='startLocation'
                isLargeInput={isLargeInput} // TODO: HP_LP_VP_LARGER_INPUTS clean up
                options={typeAheadOptions.startLocation}
                label={flightAdded ? 'Where from?' : startLocationPlaceholder(aceContent)}
                placeholder={flightAdded ? 'Where from?' : startLocationPlaceholder(aceContent)}
                value={values.startLocation}
                error={touched.startLocation && errors.startLocation}
                onChange={handleChange}
                onBlur={handleBlur}
                setValue={setFieldValue}
                onItemSelection={pipe(typeaheadFeedback, disableShadowState)}
                allowEmptySelection={dirty && !!values.startLocation}
                inputProps={{
                  onFocus: () => handleTypeaheadOnFocus(`${flightAdded ? 'start location typeahead' : 'where from typeahead'}`),
                  'data-autobot-element-id': 'HOME_RENTALCARS_STARTLOCATION_INPUT'
                }}
                onTypeaheadKeyUp={onTypeaheadKeyUp}
                disableShadowState={disableShadowState}
              />
            ) }
          </ShadowEffect>
        }
        { (hotelAdded || flightAdded || values.oneWay) &&
          <ShadowEffect forProperty='endLocation' width={flightAdded || (!hotelAdded && values.oneWay) ? [1, null, 1 / 2] : 1} px={2} mt={3} mb={2}>
            { ({ disableShadowState }) => (
              <TypeAhead
                id='endLocation'
                name='endLocation'
                isLargeInput={isLargeInput} // TODO: HP_LP_VP_LARGER_INPUTS clean up
                options={typeAheadOptions.endLocation}
                label={flightAdded ? 'Where to?' : endLocationPlaceholder(aceContent)}
                placeholder={flightAdded ? 'Where to?' : endLocationPlaceholder(aceContent)}
                value={values.endLocation}
                error={touched.endLocation && errors.endLocation}
                onChange={handleChange}
                onBlur={handleBlur}
                setValue={setFieldValue}
                onItemSelection={pipe(typeaheadFeedback, disableShadowState)}
                allowEmptySelection={dirty && !!values.endLocation}
                inputProps={{
                  onFocus: () => handleTypeaheadOnFocus(`${flightAdded ? 'where to typeahead' : 'to typeahead'}`),
                  'data-autobot-element-id': 'HOME_RENTALCARS_ENDLOCATION_INPUT'
                }}
                onTypeaheadKeyUp={onTypeaheadKeyUp}
                disableShadowState={disableShadowState}
              />
            ) }
          </ShadowEffect>
        }
      </Flex>
      {!(hotelAdded || flightAdded) && (
        <Flex>
          <Box mb={[3, null, null, null, 0]} width={[1, 'auto']}>
            <Label htmlFor='oneWay'>
              <Checkbox
                id='oneWay'
                name='oneWay'
                checked={values.oneWay}
                onBlur={handleBlur}
                onChange={(e) => {
                  setFieldValue('oneWay', e.target.checked)
                  handleChange(e)
                  handleTrackingClick('oneway click')
                }}
              />
              Return car to a different location
            </Label>
          </Box>
        </Flex>
      )}
      <Flex mx={-2} mt={-3} alignItems='center' flexWrap={['wrap', null, null, null, 'nowrap']}>
        {!(hotelAdded || flightAdded) && (
          <React.Fragment>
            <CalendarWrapperBox px={2} mb={3} width={[1 / 2, null, null, null, 3 / 12]}>
              <ShadowEffect forProperty='startDate' >
                {({ disableShadowState }) => (
                  <PopoverContainer
                    key='startDate'
                    data-calendar
                    open={showStartCalendar}
                    position='left'
                    onDismiss={pipe(handleCloseStartCalendar, disableShadowState)}
                    domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                    role='application'
                  >
                    <DateField
                      m={0}
                      id='startDate'
                      placeholder='Pick-up'
                      label='Pick-up'
                      data-autobot-element-id='HOME_RENTALCARS_STARTDATE_INPUT'
                      error={(errors.startDate && touched.startDate) ? errors.startDate : null}
                      isLargeInput={isLargeInput} // TODO: HP_RC_LARGER_INPUTS clean up
                      value={formatDate(values.startDate)}
                      onClick={(e) => {
                        setFieldTouched('startDate', false, false)
                        handleOpenStartCalendar()
                      }}
                      onFocus={(e) => {
                        setFieldTouched('startDate', false, false)
                        handleOpenStartCalendar()
                        handleCalendarOnFocus('pickup datefield')
                      }}
                      aria-expanded={showStartCalendar ? 'true' : 'false'}
                      innerRef={pickupCalendarInput}
                      type='text'
                    />
                    <Calendar
                      monthWidthPx={300}
                      startDate={values.startDate}
                      endDate={values.endDate}
                      scrollDate={values.startDate}
                      allowSameDay
                      selectTwoDates={false}
                      onChange={(_event, { startDate }) => {
                        if (startDate) {
                          setFieldTouched('startDate', true, false)
                          setFieldValue('startDate', startDate)
                          if (!touched.endDate) {
                            setFieldValue('endDate', moment(startDate).add(1, 'days').format('YYYYMMDD'))
                          }
                          focusPickupCalendarInput()
                          handleCloseStartCalendar()
                          disableShadowState()
                        }
                      }}
                      useDefaultFooter={withFooter}
                      onDismiss={pipe(focusPickupCalendarInput, handleCloseStartCalendar, disableShadowState)}
                    />
                  </PopoverContainer>
                )}
              </ShadowEffect>
            </CalendarWrapperBox>
            <Box px={2} mb={3} width={[1 / 2, null, null, null, 2 / 9]} >
              <ShadowEffect forProperty='startTime'>
                <TimeField
                  id='startTime'
                  name='startTime'
                  label='Pick-up Time'
                  data-autobot-element-id='HOME_RENTALCARS_STARTTIME_SELECT'
                  value={values.startTime}
                  style={largerInputStyle} // TODO: HP_RC_LARGER_INPUTS clean up
                  onChange={e => {
                    const endTime = pathOr('', [ 'currentTarget', 'value' ], e)
                    setFieldValue('endTime', endTime)
                    setFieldValue('startTime', endTime)
                    e.target.blur()
                  }}
                  onFocus={() => handleSelectOnFocus('pickup time select')}
                />
              </ShadowEffect>
            </Box>
            <Box px={2} mb={3} width={[1 / 2, null, null, null, 3 / 12]}>
              <ShadowEffect forProperty='endDate'>
                {({ disableShadowState }) => (
                  <PopoverContainer key='endDate'
                    data-calendar
                    open={showEndCalendar}
                    onDismiss={pipe(handleCloseEndCalendar, disableShadowState)}
                    domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                    role='application'
                    position='left'
                    left={-250}
                  >
                    <DateField
                      m={0}
                      id='endDate'
                      placeholder='Drop-off'
                      label='Drop-off'
                      data-autobot-element-id='HOME_RENTALCARS_ENDDATE_INPUT'
                      error={(errors.endDate && touched.endDate) ? errors.endDate : null}
                      isLargeInput={isLargeInput} // TODO: HP_RC_LARGER_INPUTS clean up
                      value={formatDate(values.endDate)}
                      onClick={(e) => {
                        setFieldTouched('endDate', false, false)
                        handleOpenEndCalendar()
                      }}
                      onFocus={(e) => {
                        setFieldTouched('endDate', false, false)
                        handleOpenEndCalendar()
                      }}
                      aria-expanded={showEndCalendar ? 'true' : 'false'}
                      innerRef={dropoffCalendarInput}
                      type='text'
                    />
                    <CalendarWrapperBox>
                      <Calendar
                        monthWidthPx={300}
                        startDate={values.startDate}
                        endDate={values.endDate}
                        scrollDate={values.endDate}
                        position='left'
                        allowSameDay
                        selectTwoDates={false}
                        onChange={(_event, { startDate }) => {
                          if (startDate) {
                            setFieldTouched('endDate', true, false)
                            setFieldValue('endDate', startDate)
                            focusDropoffCalendarInput()
                            handleCloseEndCalendar()
                            disableShadowState()
                          }
                        }}
                        useDefaultFooter={withFooter}
                        onDismiss={pipe(focusDropoffCalendarInput, handleCloseEndCalendar, disableShadowState)}
                      />
                    </CalendarWrapperBox>
                  </PopoverContainer>
                )}
              </ShadowEffect>
            </Box>
            <Box px={2} mb={3} width={[1 / 2, null, null, null, 2 / 9]}>
              <ShadowEffect forProperty='endTime'>
                <TimeField
                  id='endTime'
                  name='endTime'
                  label='Drop-off Time'
                  data-autobot-element-id='HOME_RENTALCARS_ENDTIME_SELECT'
                  value={values.endTime}
                  style={largerInputStyle} // TODO: HP_RC_LARGER_INPUTS clean up
                  onChange={(e) => {
                    handleChange(e)
                    e.target.blur()
                  }}
                />
              </ShadowEffect>
            </Box>
          </React.Fragment>
        )}
        {(hotelAdded || flightAdded) && (
          <React.Fragment>
            <Flex width={[1, null, null, null, 2 / 3]} flexWrap>
              <CalendarWrapperBox px={2} mb={3} width={1} id='rc-with-hotel-dates'>
                <ShadowEffect enabled={!isMobile} forProperty='startDate'>
                  {({ disableShadowState }) => (
                    <PopoverContainer
                      data-calendar
                      open={calendarOpen}
                      onDismiss={closeCalendar}
                      domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                      role='application'
                    >
                      <DateField
                        name='startDate'
                        label='Departing - Returning'
                        value={getPackagesDateRange(values)}
                        onFocus={() => {
                          openCalendar()
                          handleCalendarOnFocus()
                        }}
                        onClick={openCalendar}
                        error={parseDateError(touched, errors)}
                        isMobile={isMobile}
                        isLargeInput={isLargeInput} // TODO: HP_LP_VP_LARGER_INPUTS clean up
                        aria-expanded={calendarOpen ? 'true' : 'false'}
                      />
                      <Calendar
                        monthWidthPx={300}
                        startDate={values.startDate}
                        endDate={values.endDate}
                        onChange={(e, { startDate, endDate }) => {
                          if (startDate && endDate) {
                            closeCalendar()
                            disableShadowState()
                          }
                          setFieldValue('startDate', startDate || '')
                          setFieldValue('endDate', endDate || '')
                          setFieldValue('hotelStartDate', startDate || '')
                          setFieldValue('hotelEndDate', endDate || '')
                        }}
                        useDefaultFooter={withFooter}
                        onDismiss={pipe(closeCalendar, disableShadowState)}
                      />
                    </PopoverContainer>
                  )}
                </ShadowEffect>
              </CalendarWrapperBox>
            </Flex>
            <Flex px={2} mb={3} width={[1, null, null, null, 2 / 3]} flexWrap>
              <ShadowEffect enabled={!isMobile} forProperty='travelers-selection-readonly-input' alternativeBlurHandling>
                {({ disableShadowState }) => (
                  <TravelerSelection
                    id='travelers'
                    isLargeInput={isLargeInput} // TODO: HP_LP_VP_LARGER_INPUTS clean up
                    isUsingRoomControl={hotelAdded}
                    hasRoom={hotelAdded}
                    isUsingAdultControl
                    isUsingChildControl
                    isUsingChildAgeControl
                    isUsingControlLabel={false}
                    minAdultCount={1}
                    disclaimer={VP_DISCLAIMER}
                    initNumRooms={values.roomCount}
                    initNumAdults={values.adultCount}
                    initNumChildren={values.childrenCount || 0}
                    initNumInfants={values.infantsCount || 0}
                    initChildAges={values.childrenAges || []}
                    initDisplayLabel={travelerSelectionLabel}
                    onDoneButtonClick={disableShadowState}
                    onDismiss={disableShadowState}
                    onChange={handleTravelerSelectionChange(setFieldValue)}
                    isUsingErrorTooltip
                    error={
                      errors.childAge || errors.intlLapInfant || errors.roomCapacity
                    }
                    handleInputOnFocus={() => handleTravelerSelectionOnFocus('traveler selection')}
                  />
                )}
              </ShadowEffect>
            </Flex>
          </React.Fragment>
        )}
        <Box mt={[0, null, null, null, 22]} px={2} ml='auto' width={[1, null, null, null, 'auto']}>
          <FindYourDealButton isLargeInput={isLargeInput} /* TODO: HP_RC_LARGER_INPUTS clean up */
            formButtonProps={{ 'data-autobot-element-id': 'HOME_RENTALCARS_SUBMIT_BUTTON' }}
            currentProduct={RENTAL_CAR_PRODUCT_TYPE}
            hasMinWidth>
            Find Your Deal
          </FindYourDealButton>
          <Text fontSize={0} mt={2}>
            Book a car with free cancellation for flexibility
          </Text>
        </Box>
      </Flex>
      <Flex>
        <Box mt={[-2, null, null, null, -4]} width={[1, 'auto']}>
          <ProductTypeCheckbox
            productsOptions={productTypeCheckboxData.productsOptions}
            onChange={handleProductTypeChange}
            selectedProducts={selectedProduct}
            text='Bundle + Save: '
            gaLabelPrefix='rentalcars-'
          />
        </Box>
      </Flex>
    </form>
  )
}

RentalCarsSearchForm.displayName = 'RentalCarsSearchForm'

RentalCarsSearchForm.propTypes = {
  // Formik props
  values: PropTypes.shape({
    startLocation: PropTypes.string,
    endLocation: PropTypes.string,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    oneWay: PropTypes.bool.isRequired,
    roomCount: PropTypes.number.isRequired,
    adultCount: PropTypes.number.isRequired,
    childrenCount: PropTypes.number.isRequired,
    childrenAges: PropTypes.array.isRequired,
    tripType: PropTypes.string.isRequired,
    infantsCount: PropTypes.number
  }).isRequired,
  touched: PropTypes.object.isRequired,
  dirty: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleTrackingClick: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleFormBlur: PropTypes.func,
  handleFormFocus: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  // type ahead options/locations/dropdown items
  typeAheadOptions: PropTypes.shape({
    startLocation: PropTypes.array,
    endLocation: PropTypes.array
  }),
  // UI props
  onTypeAheadConfigChange: PropTypes.func,
  calendarOpen: PropTypes.bool,
  openCalendar: PropTypes.func,
  closeCalendar: PropTypes.func,
  showStartCalendar: PropTypes.bool,
  showEndCalendar: PropTypes.bool,
  toggleStartCalendar: PropTypes.func.isRequired,
  toggleEndCalendar: PropTypes.func.isRequired,
  handleTypeaheadOnFocus: PropTypes.func,
  handleCalendarOnFocus: PropTypes.func,
  handleSelectOnFocus: PropTypes.func,
  role: PropTypes.string,
  id: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  onTypeaheadKeyUp: PropTypes.func,
  handleTravelerSelectionOnFocus: PropTypes.func.isRequired,
  withFooter: PropTypes.bool
}

RentalCarsSearchForm.defaultProps = {
  errors: {},
  touched: {},
  values: {
    startLocation: '',
    endLocation: '',
    startDate: '',
    endDate: '',
    startTime: '12:00',
    endTime: '12:00',
    roomCount: 1,
    adultCount: 2,
    childrenCount: 0,
    childrenAges: [],
    partialStay: false,
    hotelStartDate: '',
    hotelEndDate: '',
    infantsCount: 0,
    tripType: DRIVE
  },
  typeAheadOptions: {
    startLocation: [],
    endLocation: []
  },
  handleTypeaheadOnFocus: noop,
  handleCalendarOnFocus: noop,
  handleSelectOnFocus: noop,
  onTypeaheadKeyUp: noop,
  handleTravelerSelectionOnFocus: noop,
  withFooter: true
}

export default RentalCarsSearchForm
